import { Formatters } from '../shared/lib/formatters';

// Jarvis (PDF Notes(10) #10/#11) — the operations assistant. Ask a question in
// plain English; Jarvis answers grounded in the SOP Library (POST
// /api/assetops/jarvis/ask). An audience toggle switches the voice between the
// internal team and an owner-facing answer (no internal-only detail). The SOP
// corpus is the knowledge base — write SOPs in the SOP Library to make Jarvis
// smarter. Portfolio-data + market grounding can layer in next.
const API = '/api/assetops';

interface Turn { q: string; a: string; audience: 'internal' | 'owner'; sopCount?: number; usedSources?: string[]; focus?: string | null; pending?: boolean; error?: boolean; }

const EXAMPLES = [
  'Which properties are behind pace this month?',
  'How booked is 822 Allerton over the next 30 days?',
  'Any open emergency work orders right now?',
  'What is our standard turnover cleaning process?',
];
const SOURCE_LABEL: Record<string, string> = { pace: 'pace', calendar: 'calendar/occupancy', availability: 'open nights', ops: 'open work' };

export class JarvisPage {
  private container: HTMLElement;
  private audience: 'internal' | 'owner' = 'internal';
  private turns: Turn[] = [];
  private busy = false;
  private draftQ = '';

  constructor(container: HTMLElement) { this.container = container; }

  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }

  private grounding(t: Turn): string {
    const live = (t.usedSources || []).filter(s => s !== 'sops').map(s => SOURCE_LABEL[s] || s);
    const base = `grounded in ${t.sopCount} SOP${t.sopCount === 1 ? '' : 's'}`;
    const data = live.length ? ` + live ${this.esc(live.join(', '))}` : '';
    const foc = t.focus ? ` · ${this.esc(t.focus)}` : '';
    return `${base}${data}${foc} · ${t.audience === 'owner' ? 'owner-facing' : 'internal'}`;
  }

  private inline(s: string): string {
    return Formatters.escapeHtml(s)
      .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
      .replace(/`(.+?)`/g, '<code>$1</code>');
  }

  private md(src: string): string {
    const lines = String(src || '').split('\n');
    let html = ''; let inList = false; let listType: 'ol' | 'ul' = 'ul';
    const closeList = () => { if (inList) { html += listType === 'ol' ? '</ol>' : '</ul>'; inList = false; } };
    for (const raw of lines) {
      const line = raw.replace(/\s+$/, '');
      let m: RegExpMatchArray | null;
      if ((m = line.match(/^#{1,3}\s+(.*)/))) { closeList(); html += `<div class="jv-h">${this.inline(m[1])}</div>`; continue; }
      if ((m = line.match(/^\s*\d+[.)]\s+(.*)/))) { if (!inList || listType !== 'ol') { closeList(); html += '<ol>'; inList = true; listType = 'ol'; } html += `<li>${this.inline(m[1])}</li>`; continue; }
      if ((m = line.match(/^\s*[-*•]\s+(.*)/))) { if (!inList || listType !== 'ul') { closeList(); html += '<ul>'; inList = true; listType = 'ul'; } html += `<li>${this.inline(m[1])}</li>`; continue; }
      if (!line.trim()) { closeList(); continue; }
      closeList(); html += `<p>${this.inline(line)}</p>`;
    }
    closeList();
    return html;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="jv-body"></div></div>`;
    this.draw();
  }

  private async ask(q: string): Promise<void> {
    const question = (q || '').trim();
    if (!question || this.busy) return;
    this.busy = true; this.draftQ = '';
    const turn: Turn = { q: question, a: '', audience: this.audience, pending: true };
    this.turns.push(turn);
    this.draw();
    try {
      const r = await fetch(`${API}/jarvis/ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, audience: this.audience }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Jarvis failed');
      turn.a = j.answer || '(no answer)'; turn.sopCount = j.sop_count; turn.usedSources = j.used_sources; turn.focus = j.focus; turn.pending = false;
    } catch (e: any) {
      turn.a = e.message; turn.error = true; turn.pending = false;
    } finally {
      this.busy = false; this.draw();
    }
  }

  private draw(): void {
    const body = document.getElementById('jv-body');
    if (!body) return;

    const convo = this.turns.map(t => `
      <div class="jv-turn">
        <div class="jv-q"><span class="jv-q-ic">${this.esc(t.audience === 'owner' ? '👤' : '🧑‍💼')}</span><div>${this.esc(t.q)}</div></div>
        <div class="jv-a ${t.error ? 'err' : ''}">
          <div class="jv-a-ic">✦</div>
          <div class="jv-a-body">
            ${t.pending ? `<div class="jv-typing"><span></span><span></span><span></span></div>` : this.md(t.a)}
            ${(!t.pending && !t.error && t.sopCount != null) ? `<div class="jv-grounding">${this.grounding(t)}</div>` : ''}
          </div>
        </div>
      </div>`).join('');

    const audBtn = (val: 'internal' | 'owner', label: string, sub: string) => `
      <button class="jv-aud ${this.audience === val ? 'on' : ''}" data-aud="${val}">
        <span class="jv-aud-l">${label}</span><span class="jv-aud-s">${sub}</span>
      </button>`;

    body.innerHTML = `
      <style>
        .jv-head { display:flex; align-items:baseline; gap:12px; margin-bottom:4px; }
        .jv-head h2 { margin:0; font-size:18px; font-weight:600; }
        .jv-head .sub { font-size:12px; color:var(--text-muted); }
        .jv-wrap { max-width:780px; }
        .jv-aud-row { display:flex; gap:8px; margin:14px 0 4px; }
        .jv-aud { display:flex; flex-direction:column; align-items:flex-start; gap:1px; padding:8px 14px; border:1px solid var(--border); border-radius:10px; background:#fff; cursor:pointer; text-align:left; }
        .jv-aud.on { border-color:var(--accent); box-shadow:0 0 0 1px var(--accent); background:#eaf2ff; }
        .jv-aud-l { font-size:13px; font-weight:600; color:var(--text); }
        .jv-aud-s { font-size:10.5px; color:var(--text-muted); }
        .jv-convo { margin:16px 0; display:flex; flex-direction:column; gap:18px; }
        .jv-turn { display:flex; flex-direction:column; gap:10px; }
        .jv-q { display:flex; gap:10px; align-items:flex-start; font-size:14px; font-weight:600; color:var(--text); }
        .jv-q-ic { flex:none; width:26px; height:26px; border-radius:50%; background:var(--bg-subtle); display:flex; align-items:center; justify-content:center; font-size:13px; }
        .jv-a { display:flex; gap:10px; align-items:flex-start; background:#fff; border:1px solid var(--border); border-radius:12px; padding:14px 16px; }
        .jv-a.err { background:var(--danger-soft); border-color:var(--danger); }
        .jv-a-ic { flex:none; width:26px; height:26px; border-radius:50%; background:#1e1b4b; color:#fff; display:flex; align-items:center; justify-content:center; font-size:14px; }
        .jv-a-body { font-size:13.5px; line-height:1.6; color:var(--text); flex:1; }
        .jv-a-body p { margin:0 0 8px; } .jv-a-body p:last-child { margin-bottom:0; }
        .jv-a-body ul, .jv-a-body ol { margin:0 0 8px; padding-left:20px; } .jv-a-body li { margin:3px 0; }
        .jv-a-body .jv-h { font-weight:700; margin:10px 0 4px; }
        .jv-a-body code { background:var(--bg-subtle); padding:1px 5px; border-radius:4px; font-size:12px; }
        .jv-grounding { font-size:10.5px; color:var(--text-faint); margin-top:10px; border-top:1px solid var(--border); padding-top:7px; }
        .jv-typing { display:flex; gap:4px; padding:3px 0; }
        .jv-typing span { width:7px; height:7px; border-radius:50%; background:var(--text-faint, #9ca3af); animation:jvb 1s infinite; }
        .jv-typing span:nth-child(2) { animation-delay:.15s; } .jv-typing span:nth-child(3) { animation-delay:.3s; }
        @keyframes jvb { 0%,60%,100%{opacity:.3; transform:translateY(0)} 30%{opacity:1; transform:translateY(-3px)} }
        .jv-empty { text-align:center; color:var(--text-muted); padding:24px 0; }
        .jv-examples { display:flex; flex-direction:column; gap:7px; max-width:560px; margin:14px auto 0; }
        .jv-ex { text-align:left; font-size:12.5px; padding:9px 13px; border:1px solid var(--border); border-radius:9px; background:#fff; color:var(--text); cursor:pointer; }
        .jv-ex:hover { border-color:var(--accent); }
        .jv-composer { position:sticky; bottom:0; background:var(--bg, #fff); padding-top:8px; }
        .jv-composer-inner { display:flex; gap:8px; align-items:flex-end; border:1px solid var(--border); border-radius:12px; padding:8px; background:#fff; }
        .jv-input { flex:1; border:none; outline:none; resize:none; font-size:14px; line-height:1.5; max-height:140px; min-height:24px; padding:4px 6px; font-family:inherit; }
        .jv-send { flex:none; font-size:13px; font-weight:600; padding:9px 18px; border:none; border-radius:9px; background:var(--accent); color:#fff; cursor:pointer; }
        .jv-send:disabled { opacity:.5; cursor:default; }
        .jv-hint { font-size:10.5px; color:var(--text-faint); margin-top:5px; }
      </style>
      <div class="jv-wrap">
        <div class="jv-head">
          <h2>✦ Jarvis</h2>
          <span class="sub">your operations assistant — SOPs + live portfolio data (pace, occupancy, open work)</span>
        </div>
        <div class="jv-aud-row">
          ${audBtn('internal', 'Internal', 'full operational detail')}
          ${audBtn('owner', 'Owner-facing', 'safe to share with owners')}
        </div>
        <div class="jv-convo">
          ${convo || `
            <div class="jv-empty">
              <div style="font-size:30px">✦</div>
              <div style="font-size:14px; font-weight:600; color:var(--text); margin-top:6px">Ask Jarvis about how we operate — or what's happening right now</div>
              <div style="font-size:12px; margin-top:3px">Answers draw on your SOP Library plus live portfolio data — pace vs goal, occupancy by property, and open work orders / tickets.</div>
              <div class="jv-examples">${EXAMPLES.map(q => `<button class="jv-ex" data-ex="${this.esc(q)}">${this.esc(q)}</button>`).join('')}</div>
            </div>`}
        </div>
        <div class="jv-composer">
          <div class="jv-composer-inner">
            <textarea class="jv-input" id="jv-input" rows="1" placeholder="Ask Jarvis…" ${this.busy ? 'disabled' : ''}>${this.esc(this.draftQ)}</textarea>
            <button class="jv-send" id="jv-send" ${this.busy ? 'disabled' : ''}>${this.busy ? '…' : 'Ask'}</button>
          </div>
          <div class="jv-hint">Enter to send · Shift+Enter for a new line</div>
        </div>
      </div>`;
    this.bind();
  }

  private bind(): void {
    this.container.querySelectorAll<HTMLElement>('[data-aud]').forEach(el => el.addEventListener('click', () => {
      this.audience = el.dataset.aud as 'internal' | 'owner'; this.draw();
    }));
    this.container.querySelectorAll<HTMLElement>('[data-ex]').forEach(el => el.addEventListener('click', () => {
      this.ask(el.dataset.ex!);
    }));
    const input = this.container.querySelector<HTMLTextAreaElement>('#jv-input');
    const send = this.container.querySelector<HTMLButtonElement>('#jv-send');
    const autoGrow = () => { if (input) { input.style.height = 'auto'; input.style.height = Math.min(input.scrollHeight, 140) + 'px'; } };
    input?.addEventListener('input', () => { this.draftQ = input.value; autoGrow(); });
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.ask(input.value); }
    });
    send?.addEventListener('click', () => { if (input) this.ask(input.value); });
    if (input && !this.busy) { input.focus(); autoGrow(); }
    // keep the conversation scrolled to the latest turn
    const convo = this.container.querySelector('.jv-convo');
    if (convo && this.turns.length) convo.scrollIntoView({ block: 'end' });
  }
}
